
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type SubscriberGrowthItem = {
  date: string;
  new_subscribers: number;
  unsubscribed: number;
}

export function useSubscriberGrowth(startDate: Date, endDate: Date) {
  return useQuery({
    queryKey: ["subscriber-growth", startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      try {
        // Use the rpc method to call the stored function
        const { data, error } = await supabase.rpc('get_subscriber_growth', {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        });
        
        if (error) throw error;
        
        // Ensure data is an array before mapping
        if (!Array.isArray(data)) {
          console.error("Expected array but got:", data);
          return [] as SubscriberGrowthItem[];
        }
        
        // Transform data for the frontend
        return data.map(item => ({
          date: item.date_trunc,
          new_subscribers: Number(item.new_subscribers),
          unsubscribed: Number(item.unsubscribed)
        })) as SubscriberGrowthItem[];
      } catch (error) {
        console.error("Error fetching subscriber growth:", error);
        toast.error("Failed to load subscriber growth data");
        return [] as SubscriberGrowthItem[];
      }
    },
    staleTime: 300000, // 5 minutes
  });
}

export type CampaignStats = {
  total_sends: number;
  delivered: number;
  opened: number;
  clicked: number;
  open_rate: number;
  click_rate: number;
  error_count: number;
  pending_count: number;
}

export function useCampaignStats(campaignId: string | null) {
  return useQuery({
    queryKey: ["campaign-stats", campaignId],
    queryFn: async () => {
      try {
        if (!campaignId) return null;
        
        // Use the rpc method to call the stored function
        const { data, error } = await supabase.rpc('get_campaign_stats', {
          campaign_id: campaignId
        });
        
        if (error) throw error;
        
        return data as CampaignStats;
      } catch (error) {
        console.error("Error fetching campaign stats:", error);
        toast.error("Failed to load campaign statistics");
        return null;
      }
    },
    enabled: !!campaignId,
    staleTime: 60000, // 1 minute
  });
}
